import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Anchor } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import AppleSignInButton from "@/components/AppleSignInButton";
import { isNativeApp, canShowPlans, getLastLoginEmail, rememberLoginEmail } from "@/lib/platform";

export default function Login() {
    const nav = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { setUser } = useAuth();
    const nextPath = searchParams.get("next");
    const prefilledEmail = searchParams.get("email") || getLastLoginEmail();
    const [email, setEmail] = useState(prefilledEmail);
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const native = isNativeApp();

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            rememberLoginEmail(email);
            setUser(data);
            const dest = nextPath || location.state?.from?.pathname || "/app";
            nav(dest, { replace: true });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setBusy(false);
        }
    };

    const googleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + (nextPath || "/app");
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <AuthShell title="Welcome back" subtitle="Sign in to your captain's log">
            {!native && (
                <>
                    <button
                        data-testid="login-google-btn"
                        type="button"
                        className="bm-btn bm-btn-ghost"
                        style={{ width: "100%", justifyContent: "center", padding: "12px 16px" }}
                        onClick={googleSignIn}
                    >
                        <GoogleG /> Continue with Google
                    </button>

                    <div style={{ height: 10 }} />

                    <AppleSignInButton
                        onAuthed={(data) => {
                            rememberLoginEmail(data.email);
                            setUser(data);
                            nav(nextPath || location.state?.from?.pathname || "/app", { replace: true });
                        }}
                    />

                    <Divider />
                </>
            )}
            {native && (
                <>
                    <AppleSignInButton
                        onAuthed={(data) => {
                            rememberLoginEmail(data.email);
                            setUser(data);
                            nav(nextPath || location.state?.from?.pathname || "/app", { replace: true });
                        }}
                    />
                    <Divider />
                </>
            )}

            <form onSubmit={submit} data-testid="login-form">
                <div style={{ marginBottom: 14 }}>
                    <label className="bm-label">Email</label>
                    <input
                        data-testid="login-email-input"
                        type="email"
                        className="bm-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        required
                    />
                </div>
                <div style={{ marginBottom: 18 }}>
                    <label className="bm-label">Password</label>
                    <PasswordInput
                        data-testid="login-password-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                    />
                </div>
                {err && (
                    <div data-testid="login-error" style={{ marginBottom: 14, color: "var(--bm-bad)", fontSize: 13 }}>
                        {err}
                    </div>
                )}
                <button
                    data-testid="login-submit-btn"
                    type="submit"
                    disabled={busy}
                    className="bm-btn bm-btn-primary"
                    style={{ width: "100%", padding: "12px 16px", opacity: busy ? 0.6 : 1 }}
                >
                    {busy ? "Boarding…" : "Sign in"}
                </button>
                <div style={{ marginTop: 14, textAlign: "center" }}>
                    <Link data-testid="login-forgot-link" className="bm-link" to="/forgot-password" style={{ fontSize: 13 }}>
                        Forgot password?
                    </Link>
                </div>
            </form>

            <div style={{ marginTop: 18, textAlign: "center", color: "var(--bm-text-muted)", fontSize: 13 }}>
                New here?{" "}
                <Link data-testid="login-to-register-link" className="bm-link" to={nextPath ? `/register?next=${encodeURIComponent(nextPath)}${prefilledEmail ? `&email=${encodeURIComponent(prefilledEmail)}` : ""}` : "/register"}>
                    {canShowPlans() ? "Create a free account" : "Create an account"}
                </Link>
            </div>
        </AuthShell>
    );
}

export function AuthShell({ title, subtitle, children }) {
    return (
        <div style={{ minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr", placeItems: "center", padding: "40px 24px" }}>
            <div style={{ width: "100%", maxWidth: 420 }}>
                <Link to="/" data-testid="auth-brand-link" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", marginBottom: 30 }}>
                    <Anchor size={18} color="var(--bm-gold)" />
                    <span className="bm-display" style={{ fontSize: 22 }}>Bosun's Mate</span>
                </Link>
                <h1 className="bm-serif" style={{ fontSize: 32, margin: "0 0 6px", fontWeight: 500 }}>{title}</h1>
                <p style={{ color: "var(--bm-text-muted)", margin: "0 0 28px", fontSize: 14 }}>{subtitle}</p>
                <div className="bm-card" style={{ padding: 28 }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

function Divider() {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
            <div style={{ flex: 1, borderTop: "1px solid var(--bm-border-soft)" }} />
            <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--bm-text-dim)" }}>or</span>
            <div style={{ flex: 1, borderTop: "1px solid var(--bm-border-soft)" }} />
        </div>
    );
}

function GoogleG() {
    return (
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 7.1 29.2 5 24 5c-7.5 0-13.9 4.2-17.7 9.7z" />
            <path fill="#4CAF50" d="M24 44c5.1 0 9.7-1.9 13.2-5l-6.1-5c-2 1.4-4.4 2.2-7.1 2.2-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C10 39.6 16.4 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.8l6.1 5c-.4.4 6.8-5 6.8-14.8 0-1.3-.1-2.4-.4-3.5z" />
        </svg>
    );
}
